import { move } from "@dnd-kit/helpers";
import { DragDropProvider, useDragDropMonitor } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  canLinkToPrev,
  computeQueueLabels,
  countQueueExercises,
  isQueueSection,
  reorderQueuePreservingLinks,
  reordersOwnBlock,
  wedgesIntoForeignBlock,
} from "../../../utils/workout-queue-helpers";
import SvgIcon from "../../SvgIcon";
import InsertPositionCard from "./InsertPositionCard";
import WorkoutQueueCard from "./WorkoutQueueCard";
import WorkoutQueueNoteRow from "./WorkoutQueueNoteRow";

/** Inline name form shared by "add section" and "rename section". */
function WorkoutQueueSectionForm({ initialName, onSubmit, onCancel, t }) {
  const [draft, setDraft] = useState(initialName);
  const focusedOnce = useRef(false);

  const submit = () => {
    const name = draft.trim();
    if (name) onSubmit(name);
    else onCancel();
  };

  return (
    <div className="wq-section wq-section-form">
      <input
        ref={(el) => {
          if (el && !focusedOnce.current) {
            focusedOnce.current = true;
            el.focus();
          }
        }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") onCancel();
        }}
        placeholder={t("workoutQueue.sectionNamePlaceholder")}
      />
      <button type="button" data-button-type="primary" onClick={submit}>
        {t("common:save")}
      </button>
      <button type="button" data-button-type="secondary" onClick={onCancel}>
        {t("common:cancel")}
      </button>
    </div>
  );
}

/** Named separator row between queued exercises (drag/rename/remove). */
function WorkoutQueueSectionRow({ item, index, onRename, onRemove, t }) {
  const { ref, handleRef, isDragging } = useSortable({ id: item.id, index });
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <WorkoutQueueSectionForm
        initialName={item.name}
        onSubmit={(name) => {
          onRename(name);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
        t={t}
      />
    );
  }

  return (
    <div
      ref={ref}
      className={`wq-section${isDragging ? " wq-card-dragging" : ""}`}
    >
      <span className="wq-section-name">{item.name}</span>
      <div className="wq-card-actions">
        <button
          ref={handleRef}
          type="button"
          className="wq-icon-btn wq-drag-handle"
          title={t("workoutQueue.dragToReorder")}
        >
          <SvgIcon src="/img/new-icons/drag-outline.svg" noFilterOnLight />
        </button>
        <button
          type="button"
          className="wq-icon-btn wq-icon-btn-edit"
          onClick={() => setEditing(true)}
          title={t("workoutQueue.editSection")}
        >
          <SvgIcon src="/img/new-icons/edit-pencil.svg" />
        </button>
        <button
          type="button"
          className="wq-icon-btn wq-icon-btn-danger"
          onClick={onRemove}
          title={t("workoutQueue.removeSection")}
        >
          <SvgIcon src="/img/new-icons/cross.svg" />
        </button>
      </div>
    </div>
  );
}

/**
 * Chips row between two queue items: toggle a superset link (adjacent
 * exercises only) and insert a named section.
 */
function WorkoutQueueLinkRow({
  linked,
  showSuperset,
  showSection,
  onToggleLink,
  onAddSection,
  t,
}) {
  if (!showSuperset && !showSection) return null;
  return (
    <div className={`wq-link-row${linked ? " wq-linked" : ""}`}>
      {showSuperset && (
        <button
          type="button"
          className={`wq-chip${linked ? " wq-chip-on" : ""}`}
          onClick={onToggleLink}
          data-button-type="text"
        >
          <SvgIcon
            src={`/img/new-icons/${linked ? "superset-danger" : "superset-gradient"}.svg`}
            noFilterOnLight
          />
          {linked ? t("workoutQueue.unlink") : t("workoutQueue.superset")}
        </button>
      )}
      {showSection && (
        <button
          type="button"
          className="wq-chip opacity-70"
          onClick={onAddSection}
        >
          <SvgIcon src="/img/new-icons/workout-sections-icon.svg" />
          {t("workoutQueue.section")}
        </button>
      )}
    </div>
  );
}

/**
 * Wires the queue's drag rules through `useDragDropMonitor` instead of
 * `DragDropProvider`'s `onDragOver`/`onDragEnd` props, so this component's
 * own logic runs synchronously with the drag events (the provider's props
 * route through React's `trackRendering`/`startTransition`, deferring them).
 *
 * Confirmed (via direct DOM inspection of a real repro, not just reading
 * the library) — dnd-kit relocates a dragged Card/SectionRow's actual DOM
 * node live, during the drag, by direct DOM manipulation outside React's
 * bookkeeping; it never touches the non-sortable Superset/Section link rows
 * interspersed between them, since those aren't part of its sortable
 * registry. Normally that self-heals: on drop, `items` changes and React
 * re-renders the new order. But React's array reconciliation skips
 * re-placing a child whose *virtual* index didn't shift relative to its
 * neighbors — it never re-verifies the child is still physically adjacent
 * to them in the live DOM. A link row (or even a card) that dnd-kit's
 * mid-drag reflow left sitting next to a *different* physical neighbor than
 * the one React's reconciliation expects can end up skipped by that
 * optimization, stranding it next to the wrong pair of cards — visible as
 * the reported duplicated/misplaced Superset+Section row. No cheap "did the
 * order actually change enough" check reliably predicted which drags
 * triggered this, so `onCorrected` (below) now forces a full resync on
 * every completed drop, unconditionally: a full remount sidesteps the
 * problem entirely, since a fresh mount always creates every node in the
 * current array's order from scratch, with nothing for that reconciliation
 * shortcut to skip.
 *
 * Separately (defensive hardening, not the confirmed mechanism above):
 * `useDragDropMonitor` re-subscribes its listeners — via a `useEffect` keyed
 * on the handlers object — every time the object literal passed to it gets
 * a new identity. A plain `useDragDropMonitor({ onDragOver() {...},
 * onDragEnd() {...} })` call recreates that literal on every render of this
 * component, and this component re-renders on every re-render of the whole
 * add-exercise modal, not just when the queue itself changes — so an
 * unrelated state change elsewhere in the modal while a drag is in progress
 * would tear down and re-subscribe dnd-kit's drag listeners mid-drag for no
 * reason. `handlersRef` keeps the object passed to `useDragDropMonitor`
 * referentially stable for this component's lifetime (created once), so the
 * subscription is only (re)established when the DragDropProvider itself
 * (re)mounts. `latestRef` carries the current items/callbacks into that
 * stable closure so the handlers still always act on fresh data.
 *
 * dnd-kit's same-list live drag preview isn't gated by `preventDefault` —
 * only its cross-group optimistic-reorder plugin respects
 * `event.defaultPrevented`, and it bails out immediately for a same-list
 * drag. So mid-drag, dnd-kit may still visually preview wedging the dragged
 * item into a superset it isn't allowed to join; that preview is cosmetic
 * and cannot be suppressed from here. What matters is the committed result:
 * `reorderQueuePreservingLinks` always keeps blocks atomic regardless of
 * where the drag preview showed the item landing.
 */
function WorkoutQueueDragMonitor({
  items,
  onItemsChange,
  onDropStart,
  onCorrected,
}) {
  const latestRef = useRef();
  latestRef.current = { items, onItemsChange, onDropStart, onCorrected };

  const handlersRef = useRef({
    onDragOver(event) {
      // Refuse a hover target that would either drop the dragged item
      // inside a *different* superset's span, or permute it relative to
      // its own groupmates. This only stops keyboard-driven reordering
      // (see comment above) — mouse/touch drags can still preview it, but
      // the drop is corrected regardless in onDragEnd. Carrying a superset
      // member (and its whole group) to a new spot elsewhere is never
      // refused.
      const { items } = latestRef.current;
      const draggedId = event.operation?.source?.id;
      const naiveOrder = move(items, event).map((item) => item.id);
      if (
        wedgesIntoForeignBlock(items, naiveOrder, draggedId) ||
        reordersOwnBlock(items, naiveOrder, draggedId)
      ) {
        event.preventDefault();
      }
    },
    onDragEnd(event) {
      if (event.canceled) return;
      const { items, onItemsChange, onDropStart, onCorrected } =
        latestRef.current;
      onDropStart();
      const draggedId = event.operation?.source?.id;
      const naiveOrder = move(items, event).map((item) => item.id);
      const reordered = reorderQueuePreservingLinks(
        items,
        naiveOrder,
        draggedId
      );
      // Always resync, not just when reorderQueuePreservingLinks overrode
      // dnd-kit's own proposal: dnd-kit repositions a dragged Card/
      // SectionRow's real DOM node directly, outside React's bookkeeping,
      // and React's own array-reconciliation skips re-placing a child whose
      // *virtual* index didn't shift relative to its neighbors — it never
      // re-verifies the child is still physically adjacent to them. Mixed
      // with the non-sortable link rows dnd-kit never touches, that can
      // leave one of them stranded next to whatever dnd-kit's mid-drag
      // reflow left as its physical neighbor instead of its logical one,
      // and no cheap "did the order actually change" check reliably
      // predicts which drags trigger it. A full remount sidesteps the
      // problem entirely: a fresh mount always creates every node in the
      // current array's order from scratch, so there's nothing for that
      // reconciliation shortcut to skip.
      onCorrected();
      onItemsChange(reordered, draggedId);
    },
  });

  useDragDropMonitor(handlersRef.current);
  return null;
}

/**
 * The workout-queue side panel of the add-exercise modal: shows the queued
 * exercises/sections, superset links, totals and the single Save Workout
 * action. All queue state lives in the parent modal; this component only
 * renders it and reports user intents upward.
 */
function WorkoutQueuePanel({
  items,
  editingIndex,
  destinationLabel,
  showWorkoutName,
  workoutName,
  activeUser,
  intensityMeasure,
  isRiR,
  saving,
  editingSet,
  highlightId,
  saveVariant,
  saveCount,
  insert,
  onWorkoutNameChange,
  onItemsChange,
  onToggleLink,
  onAddSection,
  onRenameSection,
  onEdit,
  onRemove,
  onSave,
  noteValue,
  noteExpanded,
  onNoteToggle,
  onNoteChange,
}) {
  const { t } = useTranslation(["coaching-portal", "common", "athlete-portal"]);
  // Index the new section will be inserted at, or null when not adding.
  const [addingAt, setAddingAt] = useState(null);
  // Bumped after every completed drop, unconditionally (see
  // WorkoutQueueDragMonitor for why this can't be done conditionally).
  // Keyed onto DragDropProvider itself: dnd-kit's per-item sortable
  // registry is keyed by item id, so remounting just the item rows (same
  // ids) left it re-attaching stale drag-preview transform data onto the
  // fresh nodes. Tearing down and recreating the whole provider destroys
  // that manager instance outright, matching what closing and reopening the
  // modal already did — see WorkoutQueueDragMonitor.
  const [resyncKey, setResyncKey] = useState(0);

  const labels = computeQueueLabels(items);
  const exerciseCount = countQueueExercises(items);

  // Insert mode: the queue lands above/below an existing exercise, so the
  // letters shown are the ones the new exercises will actually get there.
  if (insert) {
    let ordinal = 0;
    items.forEach((item, index) => {
      if (!isQueueSection(item)) {
        labels[index] = insert.view.newLetters[ordinal] ?? labels[index];
        ordinal++;
      }
    });
  }
  // A section header between circuit members would break the circuit, so
  // queued sections are skipped (server-side too) while the queue joins one.
  const sectionsAllowed = !insert?.view.join;
  const insertLetter = insert?.view.targetLetterNow;
  const saveLabel = (() => {
    if (saving) return t("workoutQueue.saving");
    if (insert && saveCount > 0) {
      return t(
        `athlete-portal:programs.insert.${
          insert.position === "above" ? "saveAbove" : "saveBelow"
        }`,
        { count: saveCount, letter: insertLetter }
      );
    }
    return saveCount > 0
      ? t("workoutQueue.saveWorkoutCount", { count: saveCount })
      : t("workoutQueue.saveWorkout");
  })();

  const renderSectionAdder = () => (
    <WorkoutQueueSectionForm
      initialName=""
      onSubmit={(name) => {
        onAddSection(addingAt, name);
        setAddingAt(null);
      }}
      onCancel={() => setAddingAt(null)}
      t={t}
    />
  );

  return (
    <aside className="workout-queue-panel">
      <div className="wq-head">
        <div className="wq-dest">
          <span className="wq-dest-label">{t("workoutQueue.addingTo")}</span>
          <span className="wq-dest-value">
            {destinationLabel}
            {insert && (
              <span className="wq-new-tag">
                {t("athlete-portal:programs.insert.newTag")}
              </span>
            )}
          </span>
        </div>
        {insert && (
          <InsertPositionCard
            position={insert.position}
            onPositionChange={insert.onPositionChange}
            targetLetter={insertLetter}
            targetName={insert.targetName}
            view={insert.view}
          />
        )}
        <div className="wq-title">
          <h3>{t("workoutQueue.title")}</h3>
          <span className="wq-count">{exerciseCount}</span>
        </div>
        <p className="wq-hint">
          {insert
            ? t(
                `athlete-portal:programs.insert.${
                  insert.position === "above" ? "landsAbove" : "landsBelow"
                }`,
                { letter: insertLetter }
              )
            : t("workoutQueue.hint")}
        </p>
        {showWorkoutName && (
          <input
            className="text-input wq-name"
            value={workoutName}
            maxLength={200}
            onChange={(e) => onWorkoutNameChange(e.target.value)}
            placeholder={t("workoutQueue.workoutNamePlaceholder")}
          />
        )}
      </div>
      {insert && !sectionsAllowed && (
        <p className="wq-insert-note">
          {t("athlete-portal:programs.insert.sectionsSkipped")}
        </p>
      )}
      <div className="wq-head-note">
        <WorkoutQueueNoteRow
          value={noteValue}
          expanded={noteExpanded}
          onToggle={onNoteToggle}
          onChange={onNoteChange}
          t={t}
        />
      </div>

      <div className="wq-list">
        {items.length === 0 ? (
          addingAt === 0 ? (
            renderSectionAdder()
          ) : (
            <div className="wq-empty-wrap">
              <p className="wq-empty">
                {t("workoutQueue.empty")}
                <b> {t("workoutQueue.emptyAdd")}</b>
              </p>
              {sectionsAllowed && (
                <div className="wq-link-row">
                  <button
                    type="button"
                    className="wq-chip opacity-70"
                    onClick={() => setAddingAt(0)}
                  >
                    <SvgIcon src="/img/new-icons/workout-sections-icon.svg" />
                    {t("workoutQueue.addWorkoutSection")}
                  </button>
                </div>
              )}
            </div>
          )
        ) : (
          <DragDropProvider key={resyncKey}>
            <WorkoutQueueDragMonitor
              items={items}
              onItemsChange={onItemsChange}
              onDropStart={() => setAddingAt(null)}
              onCorrected={() => setResyncKey((k) => k + 1)}
            />
            {items.map((item, index) => (
              <React.Fragment key={item.id}>
                {addingAt === index && renderSectionAdder()}
                {isQueueSection(item) ? (
                  <WorkoutQueueSectionRow
                    item={item}
                    index={index}
                    onRename={(name) => onRenameSection(index, name)}
                    onRemove={() => onRemove(index)}
                    t={t}
                  />
                ) : (
                  <WorkoutQueueCard
                    item={item}
                    index={index}
                    label={labels[index]}
                    isEditing={editingIndex === index}
                    inSuperset={
                      !!item.linkedToPrev ||
                      (canLinkToPrev(items, index + 1) &&
                        !!items[index + 1].linkedToPrev)
                    }
                    activeUser={activeUser}
                    intensityMeasure={intensityMeasure}
                    isRiR={isRiR}
                    editingSet={editingSet}
                    highlighted={highlightId === item.id}
                    onEdit={() => onEdit(index)}
                    onRemove={() => onRemove(index)}
                    t={t}
                  />
                )}
                {index < items.length - 1 && addingAt !== index + 1 && (
                  <WorkoutQueueLinkRow
                    linked={!!items[index + 1].linkedToPrev}
                    showSuperset={canLinkToPrev(items, index + 1)}
                    showSection={
                      sectionsAllowed &&
                      !isQueueSection(item) &&
                      !isQueueSection(items[index + 1])
                    }
                    onToggleLink={() => onToggleLink(index + 1)}
                    onAddSection={() => setAddingAt(index + 1)}
                    t={t}
                  />
                )}
              </React.Fragment>
            ))}
            {addingAt === items.length
              ? renderSectionAdder()
              : sectionsAllowed &&
                !isQueueSection(items[items.length - 1]) && (
                  <div className="wq-link-row">
                    <button
                      type="button"
                      className="wq-chip opacity-70"
                      onClick={() => setAddingAt(items.length)}
                    >
                      <SvgIcon src="/img/new-icons/workout-sections-icon.svg" />
                      {t("workoutQueue.section")}
                    </button>
                  </div>
                )}
          </DragDropProvider>
        )}
      </div>

      <div className="wq-foot">
        <button
          type="button"
          data-button-type={saveVariant === "primary" ? "primary" : "secondary"}
          className="wq-save"
          disabled={
            (saveVariant === "disabled" && !noteValue?.trim()) ||
            saving ||
            editingSet
          }
          onClick={onSave}
        >
          {saveLabel}
        </button>
      </div>
    </aside>
  );
}

export default WorkoutQueuePanel;
